import { useEffect } from "react";
import type { ExplainerInfo } from "./api";
import { Close } from "./icons";

// Motion-graphic explainers (decision 48): short videos or HTML animations that show one idea.
// Lessons list them; Today's mission (Mission.tsx) shows a Watch tile for each. Files are served from
// /explainers/ (apps/web/public/explainers/). Nothing shows until a lesson has one.

/** "1:05" for a minute or more, "45 s" under that. */
export function explainerLength(seconds?: number): string | null {
  if (!seconds) return null;
  const s = Math.round(seconds);
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** A Watch button: a pixel play tile with the explainer's title and length. */
export function WatchTile({ explainer, first, onPlay }: { explainer: ExplainerInfo; first?: boolean; onPlay: () => void }) {
  const length = explainerLength(explainer.seconds);
  return (
    <button type="button" className={`watch${first ? "" : " more"}`} onClick={onPlay} aria-label={`Watch: ${explainer.title}${length ? `, ${length}` : ""}`}>
      <span className="watch-play" aria-hidden="true">
        <i />
      </span>
      <span className="watch-text">
        {first && <span className="watch-kicker">Watch first{length ? ` · ${length}` : ""}</span>}
        <span className="watch-title">
          {explainer.title}
          {!first && length ? ` · ${length}` : ""}
        </span>
      </span>
    </button>
  );
}

export function ExplainerPlayer({ explainer, onClose }: { explainer: ExplainerInfo; onClose: () => void }) {
  const src = `/explainers/${encodeURIComponent(explainer.src)}`;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="testlab-backdrop" role="dialog" aria-modal="true" aria-label={explainer.title} onClick={onClose}>
      <div className="explainer-player" onClick={(e) => e.stopPropagation()}>
        <div className="row between">
          <div>
            <span className="eyebrow">Explainer</span>
            <h2>{explainer.title}</h2>
          </div>
          <button type="button" className="icon-close" aria-label="Close" onClick={onClose}>
            <Close size={16} />
          </button>
        </div>
        {explainer.kind === "video" ? (
          <video src={src} controls autoPlay playsInline>
            {explainer.captions && <track kind="captions" src={`/explainers/${encodeURIComponent(explainer.captions)}`} srcLang="en" label="English" default />}
          </video>
        ) : (
          <iframe src={src} title={explainer.title} sandbox="allow-scripts" />
        )}
        <p className="ink2">{explainer.caption}</p>
      </div>
    </div>
  );
}
